import { ColumnsType } from 'antd/es/table';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import {
  ContractEligibleForPriceRaise,
  getPriceRaiseCachedBox,
  getPriceRaiseContractItem,
  getPriceRaiseContractUnitPrice,
  getPriceRaiseCustomerName,
  getPriceRaisePercent,
  getPriceRaiseInputPrice,
  getNewPriceInvoicePeriod,
  isPriceRaiseAboveMaxPercent,
  getPriceRaiseProposedUnitPrice,
  isPriceRaiseReviewAgain,
  getReviewAgainPriceRaise,
  useGetContractsEligibleForPriceRaiseQuery,
} from 'entities/Contract';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { UserPermissions } from 'entities/User';
import { useAcceptPriceRaise } from 'features/AcceptPriceRaise';
import { useRejectPriceRaise } from 'features/RejectPriceRaise';
import { useTableFilterContext } from 'features/TableFilter';
import { FC, memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { InputNumber } from 'shared/ui/InputNumber';
import { ServerTable } from 'shared/ui/ServerTable';
import { RowBadge } from 'shared/ui/ServerTable/RowBadge';
import { RowSideBadge } from 'shared/ui/ServerTable/RowSideBadge';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { transformPriceRaiseListFilters } from '../utils/transformPriceRaiseListFilters';
import { PriceRaiseListFilter } from './PriceRaiseListFilter';

export const PriceRaiseTable: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currencySymbol = useGetCurrencySymbol();
  const maxPriceRaisePercent = useAppSelector(getGlobalSettings)?.maxPriceRaisePercent ?? 10;

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const [proposedPrices, setProposedPrices] = useState<Record<string, Nullable<number>>>({});
  const { openAcceptPriceRaise, acceptPriceRaiseModal } = useAcceptPriceRaise();
  const { openRejectPriceRaise, rejectPriceRaiseModal } = useRejectPriceRaise();
  const isForbiddenManagePriceRaise = useCheckPermission(UserPermissions.MANAGE_PRICE_RAISE);

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );

  const goToUserContracts = useCallback(
    (userId: string): void => {
      if (userId) {
        navigate(`${AppRoutes.USERS}/${userId}/contracts`);
      }
    },
    [navigate],
  );

  const changeProposedPrice = useCallback((contractId: string, value: Nullable<number>): void => {
    setProposedPrices((prev) => ({ ...prev, [contractId]: value }));
  }, []);

  const getEnteredPrice = useCallback(
    (contractId: string, originalBoxPrice: number): Nullable<number> => {
      if (Object.prototype.hasOwnProperty.call(proposedPrices, contractId)) {
        return proposedPrices[contractId];
      }

      return originalBoxPrice;
    },
    [proposedPrices],
  );

  const getDefaultInputPrice = useCallback(
    (contract: ContractEligibleForPriceRaise): number => {
      return getPriceRaiseInputPrice(
        getPriceRaiseContractUnitPrice(contract),
        getPriceRaiseProposedUnitPrice(contract),
        maxPriceRaisePercent,
      );
    },
    [maxPriceRaisePercent],
  );

  const isEnteredPriceAboveMax = useCallback(
    (contract: ContractEligibleForPriceRaise): boolean => {
      const enteredPrice = getEnteredPrice(contract.contractId, getDefaultInputPrice(contract));

      if (enteredPrice == null) {
        return false;
      }

      return isPriceRaiseAboveMaxPercent(getPriceRaiseContractUnitPrice(contract), enteredPrice, maxPriceRaisePercent);
    },
    [getDefaultInputPrice, getEnteredPrice, maxPriceRaisePercent],
  );

  const hasEnteredPrice = useCallback(
    (contract: ContractEligibleForPriceRaise): boolean => {
      return getEnteredPrice(contract.contractId, getDefaultInputPrice(contract)) != null;
    },
    [getDefaultInputPrice, getEnteredPrice],
  );

  const openAcceptModal = useCallback(
    (row: ContractEligibleForPriceRaise): void => {
      const newPrice = getEnteredPrice(row.contractId, getDefaultInputPrice(row));

      if (newPrice == null) {
        return;
      }

      openAcceptPriceRaise({
        contract: row,
        newPrice,
        currentPrice: getPriceRaiseContractUnitPrice(row),
        contractItem: getPriceRaiseContractItem(row),
        priceRaiseDate: row.priceRaiseDate,
        contractItemPriceRaiseId: getReviewAgainPriceRaise(row)?.contractItemPriceRaiseId,
      });
    },
    [getDefaultInputPrice, getEnteredPrice, openAcceptPriceRaise],
  );

  const openRejectModal = useCallback(
    (row: ContractEligibleForPriceRaise): void => {
      const newPrice = getEnteredPrice(row.contractId, getDefaultInputPrice(row));

      if (newPrice == null) {
        return;
      }

      openRejectPriceRaise({
        contract: row,
        newPrice,
        oldPrice: getPriceRaiseContractUnitPrice(row),
        contractItem: getPriceRaiseContractItem(row),
        contractItemPriceRaiseId: getReviewAgainPriceRaise(row)?.contractItemPriceRaiseId,
      });
    },
    [getDefaultInputPrice, getEnteredPrice, openRejectPriceRaise],
  );

  const stopRowClick = useCallback((event: MouseEvent): void => {
    event.stopPropagation();
  }, []);

  const tableActions: Array<ServerTableActions<ContractEligibleForPriceRaise>> = useMemo(
    () => [
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [setFiltersOpened, t],
  );

  const rowActions: Array<ServerTableRowActions<ContractEligibleForPriceRaise>> = useMemo(
    () => [
      {
        name: 'reject',
        label: t('Reject'),
        description: t('Reject'),
        theme: 'danger',
        onClick: openRejectModal,
        isDisabled: (item) => isForbiddenManagePriceRaise || !hasEnteredPrice(item) || isEnteredPriceAboveMax(item),
      },
      {
        name: 'accept',
        label: t('Accept'),
        description: t('Accept'),
        onClick: openAcceptModal,
        isDisabled: (item) => isForbiddenManagePriceRaise || !hasEnteredPrice(item) || isEnteredPriceAboveMax(item),
      },
    ],
    [hasEnteredPrice, isEnteredPriceAboveMax, isForbiddenManagePriceRaise, openAcceptModal, openRejectModal, t],
  );

  const columns = useMemo<ColumnsType<ContractEligibleForPriceRaise>>(
    () => [
      {
        title: <div className="text-sm">{t('Warehouse')}</div>,
        key: 'warehouse',
        render: (_, record) => (
          <div>
            <Link className="text-accent underline font-normal" to={`${AppRoutes.WAREHOUSES}/${record.warehouse.warehouseId}`}>
              {record.warehouse.name}
            </Link>
            {isPriceRaiseReviewAgain(record) && <RowBadge color="warn" title={t('Review again')} />}
            {record.noticeLeaveDate && (
              <RowSideBadge
                color="error"
                toolTipColor="white"
                toolTipFontColor="primary"
                title={`${t('Planned move-out date')}: ${getClientDateFormat(record.noticeLeaveDate)}\n${t(
                  'Move-out notice date',
                )}: ${getClientDateFormat(record.noticeGivenDate)}`}
              />
            )}
          </div>
        ),
      },
      {
        title: (
          <>
            <div className="text-sm">{t('Contract')}</div>
            <div className="text-sm">{t('Start date')}</div>
          </>
        ),
        key: 'startDate',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div
            className="text-accent underline font-normal cursor-pointer"
            onClick={() => {
              openContractDetails(record.contractId);
            }}
          >
            <div className="text-sm">#{record.contractNumber}</div>
            <div className="text-sm">{getClientDateFormat(record.startDate)}</div>
          </div>
        ),
      },
      {
        title: <div className="text-sm">{t('Customer')}</div>,
        key: 'customer',
        render: (_, record) => (
          <div
            onClick={() => {
              goToUserContracts(record.user.userId);
            }}
            className="flex flex-col"
          >
            <div className="text-sm text-accent cursor-pointer underline">{record.user.email}</div>
            <div className="font-bold text-accent cursor-pointer underline">{getPriceRaiseCustomerName(record)}</div>
          </div>
        ),
      },
      {
        title: <div className="text-sm">{t('Size / Unit')}</div>,
        key: 'box',
        render: (_, record) => {
          const cachedBox = getPriceRaiseCachedBox(record);

          return (
            <>
              <div>
                {cachedBox?.square} {getSizeCodeTypeString(cachedBox?.sizeCode?.sizeCodeType)}
              </div>
              <div className="font-normal text-sm">{cachedBox?.name}</div>
            </>
          );
        },
      },
      {
        title: <div className="text-sm">{t('Current price')}</div>,
        key: 'contractUnitPrice',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <div>
            <span className="text-sm">{t('Current contract unit price')}: </span>
            {getPriceRaiseContractUnitPrice(record)} {currencySymbol}
          </div>
        ),
      },
      {
        title: <div className="text-sm">{t('New price')}</div>,
        key: 'proposedPrice',
        render: (_, record) => {
          const currentPrice = getPriceRaiseContractUnitPrice(record);
          const originalBoxPrice = getPriceRaiseProposedUnitPrice(record);
          const enteredPrice = getEnteredPrice(record.contractId, getDefaultInputPrice(record));
          const percent = getPriceRaisePercent(currentPrice, enteredPrice ?? originalBoxPrice);
          const percentLabel = `${percent > 0 ? '+' : ''}${percent}%`;
          const boxPricePercent = getPriceRaisePercent(currentPrice, originalBoxPrice);
          const boxPricePercentLabel = `${boxPricePercent > 0 ? '+' : ''}${boxPricePercent}%`;

          return (
            <div onClick={stopRowClick}>
              <div className="text-sm font-semibold">
                {t('Current unit price')}: {originalBoxPrice} {currencySymbol} ({boxPricePercentLabel})
              </div>
              <div className="w-40 mt-1">
                <InputNumber
                  bordered
                  size="small"
                  min={0}
                  value={enteredPrice}
                  status={isEnteredPriceAboveMax(record) ? 'error' : undefined}
                  addonAfter={currencySymbol}
                  onChange={(value) => {
                    changeProposedPrice(record.contractId, value);
                  }}
                />
              </div>
              {isEnteredPriceAboveMax(record) && (
                <div className="text-error text-sm font-normal mt-1 max-w-[220px]">
                  {t('The entered value is higher than the maximum allowed percentage of {{percent}}%', {
                    percent: maxPriceRaisePercent,
                  })}
                </div>
              )}
              <div className="text-sm font-normal mt-1">({percentLabel})</div>
            </div>
          );
        },
      },
      {
        title: <div className="text-sm">{t('Price raise date')}</div>,
        key: 'priceRaiseDate',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => {
          const period = getNewPriceInvoicePeriod(record.priceRaiseDate, record);

          if (!record.priceRaiseDate) {
            return null;
          }

          return (
            <div className="text-xs">
              <div className="mb-2">{`${t('Invoicing date')}: ${getClientDateFormat(record.priceRaiseDate)}`}</div>
              {period && (
                <div>
                  {`${t('New price invoice period')}: ${getClientDateFormat(period.dateFrom)} - ${getClientDateFormat(period.dateTo)}`}
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [
      changeProposedPrice,
      currencySymbol,
      getDefaultInputPrice,
      getEnteredPrice,
      goToUserContracts,
      isEnteredPriceAboveMax,
      maxPriceRaisePercent,
      openContractDetails,
      stopRowClick,
      t,
    ],
  );

  return (
    <div className="space-y-4">
      <ServerTable
        fetchData={useGetContractsEligibleForPriceRaiseQuery}
        columns={columns}
        rowKey="contractId"
        title={t('Unprocessed')}
        tableActions={tableActions}
        tableActionsPosition="prev"
        rowActions={rowActions}
        rowActionsLimit={2}
        search
        defaultFilters={transformPriceRaiseListFilters(appliedFilters)}
        searchPlaceholder={t('Enter customer or contract number')}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        rowClassName={(record) => (record.noticeLeaveDate ? 'bg-warnLight' : '')}
      />
      <PriceRaiseListFilter statusFilterMode="unprocessed" />
      {acceptPriceRaiseModal}
      {rejectPriceRaiseModal}
    </div>
  );
});
